import { Collection, IViolation } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { SingleStreamOutputTransformation } from './single-output-stream.transformation';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { ExpressionBasedColumn } from './expression-based.column';
import { Owned } from '../base/named';
import { ExpressionBasedColumnPattern } from './expression-based.column.pattern';
import { convertColumnRefToDataFlowColumn, IDataFlowArguments, convertParametersToArguments, convertCacheSinksToArguments, DataFlowColumnArgument, DataFlowTypeArgument } from './dataflow-expression-utilities';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import { ExpressionBasedColumnBase } from './expression-based.column.base';
import { DataFlowExpression } from './dataflowexpression';
import { IValidationContext, UniqueCaseSensitiveValidator } from 'shell-models-lib';

import { DataFlowFunctionArgument } from 'app/model/dataflow/dataflow-function-argument';
import { TransformationExpressionUtils } from 'app/authoring/component/transformations/transformation.expression.utils';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';
import { PatternExpressionColumnsUtils } from 'app/authoring/component/transformations/pattern-expression-columns-utils';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { ArrayDataFlowType } from './dataflow-datatype';

@Metamodel.model({
    name: 'baseschemamodifiertransformation', // As this is abstract this will never be used
    abstract: true
})
export abstract class BaseSchemaModifierTransformation extends SingleStreamOutputTransformation {
    public static readonly typeList = [
        'string',
        'integer',
        'boolean',
        'date',
        'timestamp',
        'short',
        'double',
        'float',
        'long',
        'map'
    ];

    @Metamodel.attribute({
        transient: true
    })
    public newColumns: DataFlowColumn[];

    @Metamodel.attribute({
        transient: true
    })
    public droppedColumns: DataFlowColumn[];

    @Metamodel.attribute({
        transient: true
    })
    public unchangedColumns: DataFlowColumn[] ;

    @Metamodel.attribute({
        transient: true
    })
    public overwrittenColumns: DataFlowColumn[];

    @Metamodel.attribute({
        type: ExpressionBasedColumn.MODEL,
        collection: Collection.Array
    })
    public expressionColumns: ExpressionBasedColumnBase[] = [];

    public inspectSummaryOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.New,
            icon: () => 'svg-i-9x9-New',
            value: () => this.newColumns && this.newColumns.length || 0
        },
        {
            name: () => stringResources.TransformationInspect_Unchanged,
            value: () => this.unchangedColumns && this.unchangedColumns.length || 0
        }
    ];

    public inspectDetailsOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_Column_BasedOn,
            value: this._getBasedOnColumns
        }
    ];

    public inspectDetailsInputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_Column_UsedByColumns,
            value: this._getUsedByColumns.bind(this),
            includeFields: true
        }
    ];

    public showInspectInput = true;

    protected className: string;

    protected get columnInfoTitle(): string {
        return this.overwrittenColumns ? stringResources.TransformationInspect_Update : stringResources.TransformationInspect_Column_AggregatedAs;
    }

    public static validateOutputColumns(transformation: BaseSchemaModifierTransformation, value?: DataFlowColumn, violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.outputColumns) {
            const uniqueValidatorViolations = UniqueCaseSensitiveValidator.INSTANCE.validate(
                {},
                Metamodel.modelOf(transformation).attribute('outputColumns'),
                transformation.outputColumns().filter((x) => x.owner && x.ownerTransformation === transformation), transformation, context);
            if (uniqueValidatorViolations) {
                violations.push(...uniqueValidatorViolations);
            }
        }
        if (transformation.expressionColumns) {
            transformation.expressionColumns.forEach((expColumn) => {
                if (expColumn instanceof ExpressionBasedColumnPattern) {
                    if (expColumn.columns.length === 0) {
                        violations.push({
                            context: expColumn,
                            message: () => stringResources.RuleBasedColumnNoColumnError
                        });
                    }
                    if (expColumn.templateCondition && expColumn.templateCondition.returnType.typeString !== 'boolean' && !expColumn.regexString) {
                        violations.push({
                            context: transformation,
                            message: () => stringResources.DataFlow_ShouldReturnBoolean
                        });
                    }
                }
            });
        }
        return violations;
    }

    public static validateTransientColumns(transformation: BaseSchemaModifierTransformation, value?: DataFlowColumn[], violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.transientColumns) {
            const uniqueValidatorViolations = UniqueCaseSensitiveValidator.INSTANCE.validate(
                {},
                Metamodel.modelOf(transformation).attribute('transientColumns'),
                transformation.transientColumns.filter((x) => x.owner && x.ownerTransformation === transformation), transformation, context);
            if (uniqueValidatorViolations) {
                violations.push(...uniqueValidatorViolations);
            }
        }
        return violations;
    }

    public getColumnModificationType(column: DataFlowColumn): string {
        return '-';
    }

    public createExpressionBasedColumn(columnPattern?: ExpressionBasedColumnPattern, dataFlowColumn?: DataFlowColumn): ExpressionBasedColumn {
        const column = Owned.create<ExpressionBasedColumn>(ExpressionBasedColumn.MODEL, this);
        column.column = dataFlowColumn || Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), columnPattern || column);
        return column;
    }

    public addColumnPattern(index?: number): ExpressionBasedColumnPattern {
        const derivedColumnPattern = Owned.create<ExpressionBasedColumnPattern>(ExpressionBasedColumnPattern.MODEL, this);
        this.expressionColumns.splice(index != null ? (index + 1) : this.expressionColumns.length, 0, derivedColumnPattern);
        return derivedColumnPattern;
    }

    public addColumn(columnPattern?: ExpressionBasedColumnPattern, index?: number): ExpressionBasedColumn {
        const expressionColumn = this.createExpressionBasedColumn(columnPattern);
        if (!!columnPattern) {
            expressionColumn.dataFlowExpression.ignoreAnyTypeValidation = true;
            expressionColumn.columnNameExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, expressionColumn);
            columnPattern.columns.splice(index != null ? (index + 1) : columnPattern.columns.length, 0, expressionColumn);
        } else {
            this.expressionColumns.splice(index != null ? (index + 1) : this.expressionColumns.length, 0, expressionColumn);
        }

        return expressionColumn;
    }

    public addTransientColumn(index?: number): ExpressionBasedColumn {
        const expressionColumn = this.createExpressionBasedColumn();
        this.transientExpressionColumns.splice(index != null ? (index + 1) : this.transientColumns.length, 0, expressionColumn);
        return expressionColumn;
    }

    public getExpressions(): DataFlowExpression[] {
        const dataFlowExpressions: DataFlowExpression[] = [];

        this.expressionColumns.forEach((expressionColumn) => {
            if (expressionColumn instanceof ExpressionBasedColumn) {
                if (expressionColumn.dataFlowExpression) {
                    dataFlowExpressions.push(expressionColumn.dataFlowExpression);
                }
            } else if (expressionColumn instanceof ExpressionBasedColumnPattern) {
                if (expressionColumn.templateCondition) {
                    dataFlowExpressions.push(expressionColumn.templateCondition);
                }
                if (expressionColumn.columns) {
                    expressionColumn.columns.filter((column) => !!column.dataFlowExpression).forEach((column) => {
                        dataFlowExpressions.push(column.dataFlowExpression);
                        if (column.columnNameExpression) {
                            dataFlowExpressions.push(column.columnNameExpression);
                        }
                        if (column.fixedReturnTypeExpression) {
                            dataFlowExpressions.push(column.fixedReturnTypeExpression);
                        }
                    });

                }
            }
        });

        this.transientExpressionColumns.forEach((transientExpressionColumn) => {
            if (transientExpressionColumn instanceof ExpressionBasedColumn) {
                if (transientExpressionColumn.dataFlowExpression) {
                    dataFlowExpressions.push(transientExpressionColumn.dataFlowExpression);
                }
            }
        });

        return dataFlowExpressions;
    }

    protected updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();
        this._updateIdentifiersForColumns(this.transientExpressionColumns);
        this._updateIdentifiersForColumns(this.expressionColumns);
    }

    protected getAvailableFunctionsForExpressionColumns(): DataFlowFunctionArgument[] {
        return [].concat(...DataFlowExpressionFunctions.ALL_FUNCTIONS).concat(...DataFlowExpressionFunctions.PATTERN_FUNCTIONS);
    }

    protected getExpressionColumnsDisplayName(): string {
        return Metamodel.modelOf(this).attribute('expressionColumns').name;
    }

    protected _outputColumns(): DataFlowColumn[] {
        return this.columns.filter((column) => !column.isTransient);
    }

    protected _getUpdatedIcon(columnName: DataFlowColumn): string {
        return this.overwrittenColumns.find((v) => v.name === columnName.name) ? 'svg-i-9x9-Update'
            : this.newColumns.find((v) => v.name === columnName.name) ? 'svg-i-9x9-New' : '';
    }

    private _getUsedByColumns(column: DataFlowColumn): string {
        return TransformationExpressionUtils.getUsedByColumns(column, this.outputColumns());
    }

    private _getBasedOnColumns(column: DataFlowColumn): string {
        return column.basedOnArguments.map((basedOnColumn) => basedOnColumn.name).join(', ');
    }

    private _updateIdentifiersForColumns(columns: ExpressionBasedColumnBase[]): void {
        const availableColumns = convertColumnRefToDataFlowColumn(this.distinctInputColumnRefs);
        const transientColumns = convertColumnRefToDataFlowColumn(this.transientColumnRefs);
        const availableArgumentsForExpression: IDataFlowArguments = {
            dataFlowColumns: availableColumns,
            dataFlowTransientColumns: transientColumns,
            dataFlowFunctions: this.getAvailableFunctionsForExpressionColumns(),
            dataFlowParameters: convertParametersToArguments(this.dataflow.parameters),
            dataFlowCacheSinks: convertCacheSinksToArguments(this.dataflow.cacheSinks)
        };

        const typeColumn = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this);
        typeColumn.name = stringResources.Schema_Column_Default_Type_Placeholder;
        const tempColumn = new DataFlowColumnArgument(typeColumn.name, 'complex', typeColumn);
        const dataFlowTypes: DataFlowTypeArgument[] = [];
        BaseSchemaModifierTransformation.typeList.forEach((dataFlowType) => {
            dataFlowTypes.push(new DataFlowTypeArgument(dataFlowType, DataFlowTypesUtils.createType(dataFlowType)));
            dataFlowTypes.push(new DataFlowTypeArgument(`${dataFlowType}[]`, new ArrayDataFlowType(DataFlowTypesUtils.createType(dataFlowType))));
        });
        const typeDefinitionAvailableArguments: IDataFlowArguments = {
            dataFlowFunctions: [],
            dataFlowColumns: [tempColumn],
            dataFlowTransientColumns: [],
            dataFlowParameters: [],
            dataFlowCacheSinks: [],
            dataFlowTypes
        };

        const availableArgumentsForTemplateCondition: IDataFlowArguments =
            PatternExpressionColumnsUtils.getAvailableArgumentsForTemplateCondition(this.dataflow.parameters, this.dataflow.cacheSinks, this, false, false);
        this._updateExpressionColumnIdentifiers(columns, availableArgumentsForExpression, availableArgumentsForTemplateCondition, typeDefinitionAvailableArguments);
    }
}
